import { AccommodationDashboardStats } from '@/renderer/types/accommodation';
import React from 'react';
import { PieDataItem } from './DistributionPieChart';

interface Props {
  stats: AccommodationDashboardStats | null;
  isLoading: boolean;
  currency: string;
  /** Max booking count across all sources — used to normalise the progress bars */
  maxSourceCount: number;
  sourceBreakdown: PieDataItem[];
}

export const RevenueCard: React.FC<Props> = ({
  stats,
  isLoading,
  currency,
  maxSourceCount,
  sourceBreakdown,
}) => {
  const totalRevenue = stats?.total_revenue ?? 0;
  const totalPaid = stats?.total_paid ?? 0;
  const outstanding = Math.max(0, totalRevenue - totalPaid);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5">
      <h4 className="mb-3 text-sm font-semibold text-gray-900">
        Revenue Overview
      </h4>
      <p className="text-xs text-gray-400">Total Revenue</p>
      <p className="mt-0.5 text-2xl font-bold text-gray-900">
        {isLoading ? '…' : `${currency} ${totalRevenue.toLocaleString()}`}
      </p>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-pos-primary/10 p-3 text-center">
          <p className="text-[10px] text-pos-primary">Collected</p>
          <p className="mt-0.5 text-sm font-bold text-pos-primary">
            {isLoading ? '…' : `${currency} ${totalPaid.toLocaleString()}`}
          </p>
        </div>
        <div className="rounded-xl bg-amber-50 p-3 text-center">
          <p className="text-[10px] text-amber-600">Outstanding</p>
          <p className="mt-0.5 text-sm font-bold text-amber-700">
            {isLoading ? '…' : `${currency} ${outstanding.toLocaleString()}`}
          </p>
        </div>
      </div>

      {/* Booking source progress bars */}
      {sourceBreakdown.length > 0 && (
        <div className="mt-4 space-y-2.5">
          {(() => {
            const totalCount = sourceBreakdown.reduce(
              (sum, s) => sum + s.value,
              0,
            );
            return sourceBreakdown.slice(0, 5).map((item) => {
              // Bar width is relative to the top source (fills space nicely)
              const barPct =
                maxSourceCount > 0
                  ? Math.round((item.value / maxSourceCount) * 100)
                  : 0;
              // Label shows the actual share of total bookings
              const displayPct =
                totalCount > 0
                  ? Math.round((item.value / totalCount) * 100)
                  : 0;
              return (
                <div key={item.name}>
                  <div className="mb-1 flex items-center justify-between text-[10px]">
                    <span className="font-medium text-gray-600">
                      {item.name}
                    </span>
                    <span className="text-gray-400">{displayPct}%</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
                    <div
                      className="h-full rounded-full bg-pos-primary/60 transition-all duration-500"
                      style={{ width: `${barPct}%` }}
                    />
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}
    </div>
  );
};
